// queue.special.view — daftar kaya "Bisa Diambil" (Siap) & "Sudah Diambil".
// Port renderSiapDiambil/renderSudahDiambil v0.18 (kolom, filter, badge, aksi baris).
import { rp, esc, ageDays, tglHuman } from '@domain/format';
import { currentUser } from '@core/session';
import { canSeeModal } from '@domain/access';
import { isPiutangCara } from '@domain/money';
import { kebijakan } from '@core/settings';
import { billedNet, modalTiket, recognizedPaid } from '@domain/money';
import { garansiStatus } from '@domain/garansi';
import { storageFeeAmount, readyDaysSince } from '@domain/storagefee';
import { ulangBadge, kondisiTag, bayarTag, refundBadge, garansiColor } from './queue.tags';
import { openDetail, dlgPickup, runServisLagi, serahWAPesan } from './queue.view';
import { openWA } from '@core/wa';
import { previewNota, previewTandaTerima } from '@modules/documents/documents.view';
import { enhanceTables } from '@ui/table';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { downloadXLS } from '@ui/xls';
import type { Role } from '@core/types';
import * as repo from './queue.repo';

/** Dialog 🕘 Riwayat Servis Unit (read-only, RLS-scoped). Port riwayatUnitDialog v0.18. */
async function riwayatUnit(no: string): Promise<void> {
  let h: Awaited<ReturnType<typeof repo.unitHistory>>;
  try { h = await repo.unitHistory(no); } catch (e) { toast((e as Error).message); return; }
  const fmt = (iso: string | null): string => (iso ? tglHuman(new Date(iso)) : '-');
  const rows = h.rows.length
    ? h.rows.map((r) => `<tr><td><b>${esc(r.no)}</b>${r.isRef ? ' <span class="pill" style="background:var(--ice);color:var(--navy)">acuan</span>' : ''}</td><td>${fmt(r.tglMasuk)}</td><td>${fmt(r.tglAmbil)}</td><td>${esc(r.tindakan)}</td><td style="text-align:right">${rp(r.biaya)}</td><td>${esc(r.garansi)}</td><td>${esc(r.status)}${r.hasil && r.hasil !== 'jadi' ? ' · ' + esc(r.hasil) : ''}</td></tr>`).join('')
    : '<tr><td colspan="7" style="text-align:center;color:var(--muted)">Belum ada riwayat servis unit ini.</td></tr>';
  const bg = openModal(`<div class="modal-head"><h3>🕘 Riwayat Servis Unit — ${esc(h.custNama)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Riwayat servis unit ini (read-only). Unit: ${esc(h.unit)}.</div>
      <div style="overflow-x:auto"><table class="data" style="font-size:12px;margin-top:8px"><thead><tr><th>No. Servis</th><th>Tgl Masuk</th><th>Tgl Ambil</th><th>Tindakan</th><th style="text-align:right">Biaya</th><th>Garansi</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table></div>
    </div>
    <div class="modal-foot"><button class="btn primary" id="ru_close">Tutup</button></div>`);
  bg.querySelector('#ru_close')?.addEventListener('click', () => bg.remove());
}

/** Dialog 💰 Rincian Biaya (Sudah Diambil, gated canSeeModal). Port rincianBiaya v0.18. */
async function rincianBiaya(r: repo.SudahRow): Promise<void> {
  const t = r.t as unknown as Record<string, unknown>;
  const billed = billedNet(r.t), modal = modalTiket(r.t), profit = billed - modal;
  const dp = Number(t['dp'] ?? 0), sisa = Math.max(0, billed - dp);
  const gs = garansiStatus(r.garansi, r.garansiBase);
  const bayar = r.refund ? 'Refund' : (r.verified ? 'LUNAS · terverifikasi' : (r.lunas ? 'LUNAS · belum verifikasi' : (isPiutangCara(r.caraBayar) ? `${esc(r.caraBayar ?? 'PIUTANG').toUpperCase()}${t['jatuhTempo'] ? ' s/d ' + String(t['jatuhTempo']) : ''}` : 'Belum lunas')));
  // Riwayat sesi lama: ambil rounds (RoundDisplay punya ronde+tindakan; PaymentRound tidak). Hanya bila multi-sesi.
  let sesiHtml = '';
  if (r.roundsCount > 0) {
    try {
      const rounds = (await repo.getTicket(r.no)).rounds;
      sesiHtml = `<div class="info-row" style="border:none"><span class="k">Total keseluruhan</span><span><b>${rp(billed)}</b> (${rounds.length + 1} sesi)</span></div>
        <div class="ttl" style="font-size:13px;margin:8px 0 4px">🔁 Riwayat sesi lama</div>${rounds.map((s) => `<div class="info-row"><span class="k">Sesi ${s.ronde}</span><span>${esc(s.tindakan ?? '-')} · ${rp(Number(s.biaya ?? 0))}</span></div>`).join('')}`;
    } catch { /* riwayat opsional */ }
  }
  const row = (k: string, v: string, style = ''): string => `<div class="info-row" style="${style}"><span class="k">${k}</span><span>${v}</span></div>`;
  const bg = openModal(`<div class="modal-head"><h3>💰 Rincian Biaya — ${esc(r.no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      ${row('Tindakan', esc(r.tindakan ?? '—'))}
      ${row('Biaya ke pelanggan', `<b>${rp(billed)}</b>`)}
      ${row('DP', `${rp(dp)} · Sisa ${rp(sisa)}`)}
      ${row('Modal sparepart', rp(modal))}
      ${row('Profit', `<b style="color:${profit >= 0 ? 'var(--ok)' : 'var(--danger)'}">${rp(profit)}</b>`)}
      ${row('Pembayaran', r.refund ? `<span style="color:var(--danger)">${esc(bayar)}</span>` : esc(bayar))}
      ${row('Garansi', esc(gs.txt), 'border:none')}
      ${sesiHtml}
    </div>
    <div class="modal-foot"><button class="btn" id="rb_nota">🖨️ Nota</button><button class="btn primary" id="rb_close">Tutup</button></div>`);
  bg.querySelector('#rb_close')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#rb_nota')?.addEventListener('click', () => { bg.remove(); void previewNota(r.no); });
}

const fmtD = (iso: string | null | undefined): string => (iso ? tglHuman(new Date(iso)) : '—');
const canPickup = (role: Role): boolean => ['Owner', 'Manager', 'SPV', 'CS'].includes(role);

// Toolbar select kompak (pakai CSS .toolbar select). Wiring via data-* + delegasi.
const opt = (list: [string, string][], cur: string): string =>
  list.map(([v, l]) => `<option value="${esc(v)}" ${cur === v ? 'selected' : ''}>${esc(l)}</option>`).join('');

// ===================== SIAP DIAMBIL =====================
let siapKondisi = '', siapPeriod = 'all', siapSort = 'baru', siapQ = '';

function siapPeriodMatch(iso: string | null, mode: string): boolean {
  if (mode === 'all' || !iso) return true;
  const d = new Date(iso), now = new Date();
  if (mode === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (mode === 'week') return (now.getTime() - d.getTime()) / 86400000 <= 7;
  return true;
}

export async function renderSiap(root: HTMLElement): Promise<void> {
  const u = currentUser()!;
  const k = kebijakan();
  root.innerHTML = `<div class="ttl">Bisa Diambil</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let all: repo.SiapRow[];
  try { all = await repo.listSiap(); } catch (e) { root.innerHTML = `<div class="ttl">Bisa Diambil</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const q = siapQ.trim().toLowerCase();
  let list = all.filter((r) => !q || (`${r.no} ${r.custNama} ${r.unit} ${r.keluhan}`.toLowerCase().includes(q)))
    .filter((r) => siapPeriodMatch(r.tglISO, siapPeriod))
    .filter((r) => !siapKondisi || (r.hasil ?? '') === siapKondisi);
  list.sort((a, b) => { const av = new Date(a.tglISO ?? 0).getTime(), bv = new Date(b.tglISO ?? 0).getTime(); return siapSort === 'baru' ? bv - av : av - bv; });

  const bill = (r: repo.SiapRow): number => (r.hasil ? r.biaya : (r.biaya || r.estBiaya));
  const belumBadge = (r: repo.SiapRow): string => {
    const d = ageDays(r.tglISO); if (d <= k.ambangBelum) return '';
    let msg = 'Belum diambil ' + d + ' hari';
    if (d > 180) msg += ' · ⚠️ &gt;6 bln: di luar tanggung jawab';
    else { const bs = storageFeeAmount(readyDaysSince(r.siapISO), k.biayaSimpanBulan, k.biayaSimpanOn, { mulaiHari: k.simpanMulaiHari, lepasHari: k.simpanLepasHari, intervalHari: k.simpanIntervalHari }); if (bs > 0) msg += ' · biaya simpan ' + rp(bs); }
    return `<div style="font-size:10px;color:var(--danger);font-weight:600;margin-top:2px">⏳ ${msg}</div>`;
  };
  const act = canPickup(u.role);
  const rows = list.map((r) => `<tr>
    <td><b>${esc(r.no)}</b>${belumBadge(r)}${ulangBadge(false, r.priorRef)}</td><td>${fmtD(r.tglISO)}</td><td>${esc(r.custNama)}</td>
    <td>${esc(r.unit)}</td><td>${esc(r.keluhan)}</td>
    <td>${kondisiTag(r.hasil, r.kondisi)}${r.refund ? ' ' + refundBadge(r.refund) : ''}</td><td>${esc(r.teknisi ?? '—')}</td>
    <td>${rp(r.dp)}</td><td><b>${rp(bill(r))}</b></td><td>${fmtD(r.siapISO)}</td>
    <td style="white-space:nowrap">
      <button class="ico-btn" title="Detail" data-act="detail" data-no="${esc(r.no)}">🔍</button>
      <button class="ico-btn" title="Nota (preview)" data-act="nota" data-no="${esc(r.no)}">🖨️</button>
      ${act ? `<button class="btn" style="padding:3px 8px;font-size:12px" data-act="ulang" data-no="${esc(r.no)}">🔄 Servis Lagi</button>` : ''}
      ${act ? `<button class="btn primary" style="padding:3px 8px;font-size:12px" data-act="ambil" data-no="${esc(r.no)}">✅ Sudah Diambil</button>` : ''}
    </td></tr>`).join('');

  root.innerHTML = `<div class="crumb">Antrian · ${esc(u.role)}</div>
    <div class="ttl">Bisa Diambil ${list.length ? `<span class="pill" style="background:var(--ice);color:var(--navy)">${list.length}</span>` : ''}</div>
    <div class="scope-note">Unit selesai/siap diserahkan.</div>
    <div class="toolbar">
      <input id="siapQ" placeholder="Cari no / pelanggan / unit…" value="${esc(siapQ)}" style="flex:1;min-width:180px">
      <select id="siapKondisi">${opt([['', 'Semua kondisi'], ['jadi', 'Sudah Jadi'], ['tidakbisa', 'Tidak Bisa'], ['batal', 'Dibatalkan']], siapKondisi)}</select>
      <select id="siapPeriod">${opt([['all', 'Semua waktu'], ['week', 'Minggu ini'], ['month', 'Bulan ini']], siapPeriod)}</select>
      <select id="siapSort">${opt([['baru', 'Terbaru'], ['lama', 'Terlama']], siapSort)}</select>
    </div>
    ${list.length ? `<table class="data"><thead><tr>
      <th>No. Servis</th><th>Tgl Terima</th><th>Pemilik</th><th>Unit</th><th>Kerusakan</th><th>Kondisi</th><th>Teknisi</th><th>DP</th><th>Biaya</th><th>Tgl Ditangani</th><th>Aksi</th>
    </tr></thead><tbody>${rows}</tbody></table>` : `<div class="panel"><div class="empty">${all.length ? 'Tak ada unit cocok dengan pencarian/filter ini. <button class="btn" id="sp_clear" style="padding:3px 10px;margin-left:6px">Bersihkan filter</button>' : 'Tidak ada unit siap diambil.'}</div></div>`}`;

  const refresh = (): void => { void renderSiap(root); };
  root.querySelector('#sp_clear')?.addEventListener('click', () => { siapQ = ''; siapKondisi = ''; siapPeriod = 'all'; refresh(); });
  const sq = root.querySelector<HTMLInputElement>('#siapQ');
  if (sq) sq.addEventListener('input', () => { siapQ = sq.value; const p = sq.selectionStart; void renderSiap(root).then(() => { const n = root.querySelector<HTMLInputElement>('#siapQ'); if (n) { n.focus(); n.setSelectionRange(p, p); } }); });
  const wireSel = (id: string, set: (v: string) => void): void => { const el = root.querySelector<HTMLSelectElement>('#' + id); if (el) el.onchange = (): void => { set(el.value); refresh(); }; };
  wireSel('siapKondisi', (v) => { siapKondisi = v; });
  wireSel('siapPeriod', (v) => { siapPeriod = v; });
  wireSel('siapSort', (v) => { siapSort = v; });
  root.querySelectorAll<HTMLElement>('button[data-act]').forEach((b) => b.addEventListener('click', () => {
    const no = b.dataset.no ?? '';
    switch (b.dataset.act) {
      case 'detail': void openDetail(no, refresh); break;
      case 'nota': void previewNota(no); break;
      case 'ulang': void runServisLagi(no, refresh); break;
      case 'ambil': void dlgPickup(no, refresh); break;
    }
  }));
}

// ===================== SUDAH DIAMBIL =====================
let sdStatus = '', sdBayar = '', sdGaransi = '', sdBulan = 0, sdTahun = 0, sdSort = 'baru', sdShowBiaya = false, sdQ = '';
let sdRecog = ''; // E-B: filter pengakuan Finance (belum diakui / sudah)

function sdPeriodMatch(r: repo.SudahRow): boolean {
  if (!sdBulan && !sdTahun) return true;
  const iso = r.tglAmbilISO ?? r.tglISO; if (!iso) return false;
  const d = new Date(iso);
  if (sdTahun && d.getFullYear() !== sdTahun) return false;
  if (sdBulan && d.getMonth() + 1 !== sdBulan) return false;
  return true;
}

// E-C Export XLS: downloadXLS di @ui/xls (dipakai bersama Status Pembayaran).

// Label Kondisi versi teks-polos untuk ekspor (kondisiTag mengembalikan pill HTML).
function kondisiText(hasil: string | null, kondisi: string | null): string {
  if (hasil === 'jadi') return 'Sudah Jadi';
  if (hasil === 'tidakbisa') return 'Tidak Bisa';
  if (hasil === 'batal') return 'Dibatalkan';
  return kondisi ?? '-';
}

/** Bangun tabel Excel-HTML dari daftar Sudah Diambil yang SEDANG terfilter (kolom = tampilan). */
function sudahXLS(list: repo.SudahRow[], canModal: boolean): string {
  const head = ['No. Servis', 'Tgl Terima', 'Pemilik', 'Unit', 'Kerusakan', 'Kondisi', 'Ket/Tindakan', 'Teknisi',
    'Biaya', ...(canModal ? ['Modal', 'Profit'] : []), 'Bayar', 'Tgl Ambil', 'Garansi'];
  const thead = '<tr>' + head.map((h) => `<th>${esc(h)}</th>`).join('') + '</tr>';
  const body = list.map((r) => {
    const billed = billedNet(r.t), modal = modalTiket(r.t), profit = billed - modal;
    const sesi = r.roundsCount + 1;
    const cells = [
      esc(r.no) + (r.roundsCount > 0 ? ` (${sesi} sesi)` : ''),
      fmtD(r.tglISO), esc(r.custNama), esc(r.unit), esc(r.keluhan),
      kondisiText(r.hasil, r.kondisi), esc(r.tindakan ?? r.kondisi ?? '-'), esc(r.teknisi ?? '-'),
      `<td class="r">${rp(billed)}</td>`,
      ...(canModal ? [`<td class="r">${rp(modal)}</td>`, `<td class="r">${rp(profit)}</td>`] : []),
      r.lunas ? (r.verified ? 'Lunas (verif)' : 'Lunas') : 'Belum lunas',
      fmtD(r.tglAmbilISO), esc(garansiStatus(r.garansi, r.garansiBase).txt),
    ];
    return '<tr>' + cells.map((c) => (c.startsWith('<td') ? c : `<td>${c}</td>`)).join('') + '</tr>';
  }).join('');
  return `<h2>iFiCs — Sudah Diambil</h2><p>Total ${list.length} unit</p>`
    + `<table><thead>${thead}</thead><tbody>${body}</tbody></table>`;
}

// Sort header + resize kolom → helper universal `enhanceTables` (@ui/table). Global observer juga menangani ini.

export async function renderSudah(root: HTMLElement): Promise<void> {
  const u = currentUser()!;
  const showB = sdShowBiaya, canModal = canSeeModal(u.role), showModal = canModal && sdShowBiaya;
  root.innerHTML = `<div class="ttl">Sudah Diambil</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let all: repo.SudahRow[];
  try { all = await repo.listSudah(); } catch (e) { root.innerHTML = `<div class="ttl">Sudah Diambil</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const q = sdQ.trim().toLowerCase();
  let list = all.filter((r) => !q || (`${r.no} ${r.custNama} ${r.unit} ${r.keluhan} ${r.sn}`.toLowerCase().includes(q))) // E-A: +SN/IMEI
    .filter(sdPeriodMatch)
    .filter((r) => !sdBayar || (sdBayar === 'lunas' ? r.lunas : !r.lunas))
    .filter((r) => { if (!sdGaransi) return true; const gt = garansiStatus(r.garansi, r.garansiBase).txt; const aktif = gt.startsWith('Sisa') || gt === 'Lifetime'; return sdGaransi === 'aktif' ? aktif : !aktif; }) // persis v0.18: hanya "Sisa N hari" (+Lifetime) = masih garansi; base-null/lewat/tidak-ada = tidak
    .filter((r) => { if (!sdStatus) return true; const ulang = r.servisUlang || r.roundsCount > 0 || !!r.priorRef; if (sdStatus === 'ulang') return ulang; if (sdStatus === 'refund') return !!r.refund; if (sdStatus === 'normal') return !ulang && !r.refund; return true; })
    .filter((r) => { if (!sdRecog) return true; const unrec = Math.max(0, billedNet(r.t) - recognizedPaid(r.t)); return sdRecog === 'belum' ? unrec > 0 : unrec <= 0; }); // E-B pengakuan Finance (reuse unrec logika v0.18)
  list.sort((a, b) => { const av = new Date(a.tglAmbilISO ?? a.tglISO ?? 0).getTime(), bv = new Date(b.tglAmbilISO ?? b.tglISO ?? 0).getTime(); return sdSort === 'baru' ? bv - av : av - bv; });

  const rows = list.map((r) => {
    const gs = garansiStatus(r.garansi, r.garansiBase);
    const billed = billedNet(r.t), profit = billed - modalTiket(r.t), unrec = Math.max(0, billed - recognizedPaid(r.t));
    const sesi = r.roundsCount + 1;
    return `<tr>
      <td><b>${esc(r.no)}</b>${r.roundsCount > 0 ? ` <span class="pill" style="background:var(--ice);color:var(--navy)" title="${sesi} sesi servis">🔁${sesi}s</span>` : ''}${r.refund ? ' ' + refundBadge(r.refund) : ''}${ulangBadge(false, r.priorRef)}</td>
      <td>${fmtD(r.tglISO)}</td><td>${esc(r.custNama)}</td><td>${esc(r.unit)}</td><td>${esc(r.keluhan)}</td>
      <td>${kondisiTag(r.hasil, r.kondisi)}</td><td>${esc(r.tindakan ?? r.kondisi ?? '—')}</td><td>${esc(r.teknisi ?? '—')}</td>
      ${showB ? `<td><b>${rp(billed)}</b>${unrec > 0 ? `<br><span class="pill" title="Belum diverifikasi Finance — omzet & komisi diakui setelah verifikasi di Status Pembayaran" style="background:var(--warnbg);color:var(--warn);font-size:9px;white-space:nowrap">⏳ ${rp(unrec)} blm diakui</span>` : ''}</td>` : ''}
      ${showModal ? `<td>${rp(modalTiket(r.t))}</td><td style="color:${profit >= 0 ? 'var(--ok)' : 'var(--danger)'}">${rp(profit)}</td>` : ''}
      <td>${bayarTag(r)}</td><td>${fmtD(r.tglAmbilISO)}</td>
      <td><span style="color:${garansiColor(gs)};font-weight:600">${esc(gs.txt)}</span></td>
      <td style="white-space:nowrap">
        <button class="ico-btn" title="Detail" data-act="detail" data-no="${esc(r.no)}">🔍</button>
        ${showModal ? `<button class="ico-btn" title="Rincian Biaya" data-act="rincian" data-no="${esc(r.no)}">💰</button>` : ''}
        <button class="ico-btn" title="Riwayat Unit" data-act="riwayat" data-no="${esc(r.no)}">🕘</button>
        <button class="ico-btn" title="Nota + Kartu Garansi" data-act="nota" data-no="${esc(r.no)}">🖨️</button>
        <button class="ico-btn" title="Kirim WA ke pelanggan (terima kasih + survei + garansi + sosmed)" data-act="wa" data-no="${esc(r.no)}">💬</button>
        ${(r.servisUlang || r.roundsCount > 0) ? `<button class="ico-btn" title="Tanda Terima GARANSI" data-act="tandagar" data-no="${esc(r.no)}">📄</button>` : ''}
        <button class="ico-btn" title="Servis Lagi (Garansi)" data-act="ulang" data-no="${esc(r.no)}">🔄</button>
      </td></tr>`;
  }).join('');

  const yearOpts = ((): string => { let o = '<option value="0">Semua tahun</option>'; const cy = new Date().getFullYear(); for (let y = 2024; y <= cy + 1; y++) o += `<option value="${y}" ${sdTahun === y ? 'selected' : ''}>${y}</option>`; return o; })();
  const MS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  root.innerHTML = `<div class="crumb">Antrian · ${esc(u.role)}</div>
    <div class="ttl">Sudah Diambil ${list.length ? `<span class="pill" style="background:var(--ice);color:var(--navy)">${list.length}</span>` : ''}</div>
    <div class="scope-note">Riwayat unit terserah + status garansi & pembayaran.${showModal ? ' <b>Biaya = total tagihan</b> (semua sesi); pengakuan omzet/komisi mengikuti verifikasi Finance.' : ' modal/profit hanya Owner/Manager/SPV/Finance.'}</div>
    <div class="toolbar">
      <input id="sdQ" placeholder="Cari no / pelanggan / unit…" value="${esc(sdQ)}" style="flex:1;min-width:150px">
      <select id="sdStatus">${opt([['', 'Semua status'], ['ulang', '🔁 Servis Ulang'], ['refund', '💸 Refund'], ['normal', 'Normal']], sdStatus)}</select>
      <select id="sdBayar">${opt([['', 'Semua bayar'], ['lunas', 'Lunas'], ['belum', 'Belum Lunas']], sdBayar)}</select>
      <select id="sdGaransi">${opt([['', 'Semua garansi'], ['aktif', 'Masih Garansi'], ['habis', 'Tidak/Lewat Garansi']], sdGaransi)}</select>
      <select id="sdRecog">${opt([['', 'Semua pengakuan'], ['belum', '⏳ Belum diakui'], ['sudah', 'Sudah diakui']], sdRecog)}</select>
      <select id="sdBulan"><option value="0">Semua bulan</option>${MS.map((m, i) => `<option value="${i + 1}" ${sdBulan === i + 1 ? 'selected' : ''}>${m}</option>`).join('')}</select>
      <select id="sdTahun">${yearOpts}</select>
      <select id="sdSort">${opt([['baru', 'Terbaru'], ['lama', 'Terlama']], sdSort)}</select>
      <button class="btn ${sdShowBiaya ? 'primary' : ''}" id="sdBiayaBtn" style="padding:5px 10px;font-size:12.5px">💰 ${sdShowBiaya ? 'Sembunyikan Biaya' : 'Biaya'}</button>
      ${list.length ? `<button class="btn" id="sdXlsBtn" style="padding:5px 10px;font-size:12.5px">📥 Excel</button>` : ''}
    </div>
    ${list.length ? `<table class="data"><thead><tr>
      <th>No. Servis</th><th>Tgl Terima</th><th>Pemilik</th><th>Unit</th><th>Kerusakan</th><th>Kondisi</th><th>Ket/Tindakan</th><th>Teknisi</th>${showB ? '<th>Biaya</th>' : ''}${showModal ? '<th>Modal</th><th>Profit</th>' : ''}<th>Bayar</th><th>Tgl Ambil</th><th>Garansi</th><th>Aksi</th>
    </tr></thead><tbody>${rows}</tbody></table>` : `<div class="panel"><div class="empty">${all.length ? 'Tak ada unit cocok dengan pencarian/filter ini. <button class="btn" id="sd_clear" style="padding:3px 10px;margin-left:6px">Bersihkan filter</button>' : 'Belum ada unit yang sudah diambil.'}</div></div>`}`;

  enhanceTables(root); // header klik-sort + resize kolom (universal)
  const refresh = (): void => { void renderSudah(root); };
  root.querySelector('#sd_clear')?.addEventListener('click', () => { sdQ = ''; sdStatus = ''; sdBayar = ''; sdGaransi = ''; sdRecog = ''; sdBulan = 0; sdTahun = 0; refresh(); });
  const sq = root.querySelector<HTMLInputElement>('#sdQ');
  if (sq) sq.addEventListener('input', () => { sdQ = sq.value; const p = sq.selectionStart; void renderSudah(root).then(() => { const n = root.querySelector<HTMLInputElement>('#sdQ'); if (n) { n.focus(); n.setSelectionRange(p, p); } }); });
  const wireSel = (id: string, set: (v: string) => void): void => { const el = root.querySelector<HTMLSelectElement>('#' + id); if (el) el.onchange = (): void => { set(el.value); refresh(); }; };
  wireSel('sdStatus', (v) => { sdStatus = v; });
  wireSel('sdBayar', (v) => { sdBayar = v; });
  wireSel('sdGaransi', (v) => { sdGaransi = v; });
  wireSel('sdRecog', (v) => { sdRecog = v; });
  wireSel('sdBulan', (v) => { sdBulan = +v; });
  wireSel('sdTahun', (v) => { sdTahun = +v; });
  wireSel('sdSort', (v) => { sdSort = v; });
  root.querySelector('#sdBiayaBtn')?.addEventListener('click', () => { sdShowBiaya = !sdShowBiaya; refresh(); });
  root.querySelector('#sdXlsBtn')?.addEventListener('click', () => { downloadXLS('Sudah_Diambil.xls', sudahXLS(list, canModal)); toast('📥 Excel diunduh'); });
  root.querySelectorAll<HTMLElement>('button[data-act]').forEach((b) => b.addEventListener('click', () => {
    const no = b.dataset.no ?? '';
    switch (b.dataset.act) {
      case 'detail': void openDetail(no, refresh); break;
      case 'rincian': { const rr = list.find((x) => x.no === no); if (rr) void rincianBiaya(rr); break; }
      case 'riwayat': void riwayatUnit(no); break;
      case 'nota': void previewNota(no); break;
      case 'wa': { const rr = list.find((x) => x.no === no); void (async () => { try { const m = await serahWAPesan(no, rr?.garansi ?? null); if (!m) { toast('Nomor WA pelanggan belum ada.'); return; } openWA(m.wa, m.pesan); } catch (e) { toast((e as Error).message); } })(); break; }
      case 'tandagar': void previewTandaTerima(no, { garansi: true }); break;
      case 'ulang': void runServisLagi(no, refresh); break;
    }
  }));
}
